// Phase 11 — persistent floating clipboard tray.
//
// Always mounted from __root.tsx. Hidden until the user collects their first
// symbol. Survives navigation (lives outside the route tree) and reloads
// (state is mirrored to localStorage by `clipboard-tray.ts`).

import { useEffect, useState } from "react";
import {
  clearTray,
  removeFromTray,
  reorderTray,
  TRAY_LIMIT,
  useTray,
} from "@/lib/clipboard-tray";
import { copyText } from "@/lib/copy";
import { getSymbolName } from "@/data/symbol-names";

export function ClipboardTray() {
  const tray = useTray();
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [flash, setFlash] = useState<"all" | string | null>(null);
  const [separator, setSeparator] = useState<"" | " " | "\n">("");

  // Avoid SSR/CSR hydration mismatch — tray is empty server-side, may not be
  // empty client-side once localStorage rehydrates.
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  if (tray.items.length === 0) return null;

  const copyAll = async () => {
    const ok = await copyText(tray.items.join(separator), {
      source: "tray-all",
    });
    if (ok) {
      setFlash("all");
      window.setTimeout(() => setFlash((f) => (f === "all" ? null : f)), 1200);
    }
  };

  const copyOne = async (symbol: string) => {
    const ok = await copyText(symbol, { source: "tray-item", symbol });
    if (ok) {
      setFlash(symbol);
      window.setTimeout(() => setFlash((f) => (f === symbol ? null : f)), 1000);
    }
  };

  return (
    <div className="clipboard-tray-root">
      {expanded ? (
        <div className="glass clipboard-tray-panel">
          <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-sm font-semibold">Tray</span>
              <span className="text-xs text-muted-foreground">
                {tray.items.length} / {TRAY_LIMIT}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Collapse tray"
              className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              ▾ Hide
            </button>
          </div>

          <ul className="clipboard-tray-list">
            {tray.items.map((s, i) => (
              <li
                key={`${s}-${i}`}
                className="flex items-center gap-2 rounded-md border border-border/60 bg-surface/40 px-2 py-1.5"
              >
                <button
                  type="button"
                  onClick={() => copyOne(s)}
                  aria-label={`Copy ${getSymbolName(s)}`}
                  title={getSymbolName(s)}
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-border bg-surface/60 text-lg hover:bg-secondary"
                >
                  <span aria-hidden="true">{s}</span>
                </button>
                <span className="min-w-0 flex-1 truncate text-xs text-foreground/80">
                  {flash === s ? "Copied!" : getSymbolName(s)}
                </span>
                <div className="flex flex-none items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => reorderTray(i, i - 1)}
                    disabled={i === 0}
                    aria-label={`Move ${getSymbolName(s)} up`}
                    className="h-7 w-7 rounded text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => reorderTray(i, i + 1)}
                    disabled={i === tray.items.length - 1}
                    aria-label={`Move ${getSymbolName(s)} down`}
                    className="h-7 w-7 rounded text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFromTray(s)}
                    aria-label={`Remove ${getSymbolName(s)} from tray`}
                    className="h-7 w-7 rounded text-xs text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                  >
                    ✕
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              Join with:
              <select
                value={separator}
                onChange={(e) => setSeparator(e.target.value as "" | " " | "\n")}
                aria-label="Separator between symbols when copying all"
                className="rounded border border-border bg-surface/60 px-1.5 py-0.5 text-xs text-foreground"
              >
                <option value="">none</option>
                <option value=" ">space</option>
                <option value={"\n"}>newline</option>
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => clearTray()}
                className="rounded-md border border-border bg-surface/60 px-2.5 py-1.5 text-xs font-medium hover:bg-secondary"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={copyAll}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
              >
                {flash === "all" ? "Copied all!" : `Copy all (${tray.items.length})`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-label={expanded ? "Collapse clipboard tray" : "Open clipboard tray"}
        aria-expanded={expanded}
        className="glass clipboard-tray-fab"
      >
        <span className="flex -space-x-2">
          {tray.items.slice(-3).map((s, i) => (
            <span
              key={i}
              aria-hidden="true"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-surface/80 text-base"
            >
              {s}
            </span>
          ))}
        </span>
        <span className="ml-2 text-sm font-semibold">
          Tray · {tray.items.length}
        </span>
        <span aria-hidden="true" className="ml-1.5 text-xs text-muted-foreground">
          {expanded ? "▾" : "▴"}
        </span>
      </button>
    </div>
  );
}
